/**
 * The palette registry (PLAN §12.1): providers and commands register here and
 * the palette reads them. Pure module state, no React.
 *
 * Duplicates (provider id, prefix word, sigil, command id) THROW outside
 * production, so two features cannot silently fight over `b:`; in production
 * the later registration is ignored with a warning instead of taking the app
 * down.
 */

const providers = new Map(); // id -> Provider
const prefixIndex = new Map(); // word | sigil -> provider id
const commands = new Map(); // id -> Command
const listeners = new Set();

const ID_RE = /^[a-z][a-z-]*$/;
const WORD_RE = /^[a-z][a-z0-9-]*$/;

function isProd() {
  return process.env.NODE_ENV === 'production';
}

function conflict(message) {
  if (!isProd()) throw new Error(`palette registry: ${message}`);
  // eslint-disable-next-line no-console
  console.warn(`palette registry: ${message}`);
  return null;
}

function emit() {
  for (const fn of [...listeners]) {
    try {
      fn();
    } catch {
      /* a listener must not break registration */
    }
  }
}

/**
 * @param {import('./types').Provider} p
 * @returns {() => void} unregister
 */
export function registerProvider(p) {
  if (!p || typeof p !== 'object') throw new Error('palette registry: provider object required');
  if (!ID_RE.test(String(p.id || ''))) throw new Error(`palette registry: bad provider id "${p.id}"`);
  if (typeof p.search !== 'function' || typeof p.actions !== 'function' || typeof p.available !== 'function') {
    throw new Error(`palette registry: provider "${p.id}" needs search, actions and available`);
  }
  if (providers.has(p.id)) return conflict(`duplicate provider id "${p.id}"`) || (() => {});
  const words = (p.prefixes || []).map((w) => String(w).toLowerCase());
  const sigils = (p.sigils || []).map(String);
  for (const w of words) {
    if (!WORD_RE.test(w)) throw new Error(`palette registry: bad prefix "${w}" on "${p.id}"`);
    if (prefixIndex.has(w)) return conflict(`prefix "${w}" of "${p.id}" is taken by "${prefixIndex.get(w)}"`) || (() => {});
  }
  for (const s of sigils) {
    if (s.length !== 1) throw new Error(`palette registry: sigil "${s}" must be one character`);
    if (prefixIndex.has(s)) return conflict(`sigil "${s}" of "${p.id}" is taken by "${prefixIndex.get(s)}"`) || (() => {});
  }
  providers.set(p.id, p);
  for (const w of words) prefixIndex.set(w, p.id);
  for (const s of sigils) prefixIndex.set(s, p.id);
  emit();
  let done = false;
  return () => {
    if (done) return;
    done = true;
    if (providers.get(p.id) !== p) return;
    providers.delete(p.id);
    for (const [k, v] of [...prefixIndex]) if (v === p.id) prefixIndex.delete(k);
    try {
      p.dispose?.();
    } catch {
      /* ignore */
    }
    emit();
  };
}

/**
 * @param {import('./types').Command} c
 * @returns {() => void} unregister
 */
export function registerCommand(c) {
  if (!c || !c.id || typeof c.run !== 'function' || !c.titleKey) {
    throw new Error('palette registry: command needs id, titleKey and run');
  }
  if (commands.has(c.id)) return conflict(`duplicate command id "${c.id}"`) || (() => {});
  commands.set(c.id, c);
  emit();
  let done = false;
  return () => {
    if (done) return;
    done = true;
    if (commands.get(c.id) !== c) return;
    commands.delete(c.id);
    emit();
  };
}

/** Providers sorted by group order (§2.4), then id. */
export function getProviders() {
  return [...providers.values()].sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || (a.id < b.id ? -1 : 1));
}

export function getProvider(id) {
  return providers.get(id) || null;
}

export function getCommands() {
  return [...commands.values()];
}

/** 'b' | 'boards' | '#' → provider (case-insensitive for words). */
export function resolvePrefix(word) {
  if (word == null) return null;
  const w = String(word);
  const id = prefixIndex.get(w) || prefixIndex.get(w.toLowerCase());
  return id ? providers.get(id) || null : null;
}

export function subscribeRegistry(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Tests only: forget everything. */
export function resetRegistryForTests() {
  for (const p of providers.values()) {
    try {
      p.dispose?.();
    } catch {
      /* ignore */
    }
  }
  providers.clear();
  prefixIndex.clear();
  commands.clear();
  emit();
}
